import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { i18n } from "@/lib/i18n";
import { organizationService } from "../services";
import { kpiTargetsStore } from "@/features/esg";
import { companyStore } from "@/features/company/stores/company.store";
import {
  isSanitizedOrganizationImportPayload,
  sanitizeOrganizationImportFile,
} from "../utils/excelImportSanitizer";

const LEVEL_REGEX = /^level_(\d+)$/;
const SELECTED_ORG_STORAGE_KEY = "nxstart_selected_org";

export const readStoredOrg = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem(SELECTED_ORG_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeStoredOrg = (id, name) => {
  if (typeof window === "undefined") return;
  try {
    if (id) {
      localStorage.setItem(
        SELECTED_ORG_STORAGE_KEY,
        JSON.stringify({ id, name }),
      );
    } else {
      localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
};

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const normalizeMessage = (error, fallback) => {
  const message = error?.response?.data?.message || error?.message || fallback;
  if (Array.isArray(message)) {
    return message.join(", ");
  }
  return message;
};

const resolvePayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

const downloadBlob = (blob, filename) => {
  const validBlob = blob instanceof Blob ? blob : null;
  if (!validBlob) {
    throw new Error("Template download failed. Invalid file response.");
  }

  const url = window.URL.createObjectURL(validBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

class OrganizationStore {
  loading = false;
  saving = false;
  deletingId = null;
  togglingId = null;
  error = "";
  lookupError = "";

  downloadingTemplate = false;
  importing = false;

  tree = [];
  businessTypeTree = [];
  countryTree = [];
  unitsById = new Map();
  selectedId = null;
  lookupsLoading = false;
  countryOptions = [];
  regionOptions = [];
  orgList = [];

  filters = {
    companyId: "All",
    level: "All",
    search: "",
  };
  pageIndex = 1;
  pageSize = 10;

  searchInput = "";
  _searchDebounceTimer = null;

  constructor() {
    makeAutoObservable(this);
    const { id } = readStoredOrg();
    this.selectedId = id;
  }

  get items() {
    return Array.from(this.unitsById.values());
  }

  get parentUnit() {
    return (
      this.items.find((unit) => unit.unitLevel === "parent_company") ?? null
    );
  }

  get maxLevel() {
    return this.items.reduce((max, unit) => {
      const level = getLevelNumber(unit.unitLevel);
      return level && level > max ? level : max;
    }, 0);
  }

  getLevelUnits = (levelNumber) => {
    return this.items.filter(
      (unit) => getLevelNumber(unit.unitLevel) === levelNumber,
    );
  };

  get selectedUnit() {
    if (!this.selectedId) return null;
    return this.unitsById.get(this.selectedId) ?? null;
  }

  selectUnit = (unitId) => {
    this.selectedId = unitId || null;
    const orgName = this.unitsById.get(unitId)?.name || null;
    writeStoredOrg(this.selectedId, orgName);
  };

  resetError = () => {
    this.error = "";
  };

  get companyNameById() {
    const map = new Map();
    const list = Array.isArray(companyStore.companies)
      ? companyStore.companies
      : [];
    list.forEach((c) => {
      const id = String(c?.id ?? c?._id ?? c?.companyId ?? "");
      if (!id) return;
      map.set(id, String(c?.companyName ?? c?.name ?? c?.code ?? "—"));
    });
    return map;
  }

  fetchLookups = async () => {
    runInAction(() => {
      this.lookupsLoading = true;
      this.lookupError = "";
    });
    try {
      const data = await organizationService.fetchLookups({
        throwOnError: false,
      });
      if (!data) {
        throw new Error("Unable to load location lookups.");
      }
      const payload = resolvePayload(data) || {};
      runInAction(() => {
        this.countryOptions = Array.isArray(payload.countries)
          ? payload.countries
          : [];
        this.regionOptions = Array.isArray(payload.regions)
          ? payload.regions
          : [];
      });
      return payload;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to load location lookups.",
      );
      runInAction(() => {
        this.lookupError = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.lookupsLoading = false;
      });
    }
  };

  fetchHierarchy = async (options = {}) => {
    const { selectId, groupBy } = options;
    runInAction(() => {
      this.loading = true;
      this.error = "";
    });
    try {
      const data = await organizationService.fetchHierarchy({
        throwOnError: false,
        body: groupBy ? { groupBy } : {},
      });
      if (!data) {
        throw new Error("Unable to load organization structure.");
      }
      const payload = resolvePayload(data) || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const tree = Array.isArray(payload.tree) ? payload.tree : [];
      runInAction(() => {
        if (groupBy === "businessType") {
          this.businessTypeTree = tree;
        } else if (groupBy === "country") {
          this.countryTree = tree;
        } else {
          this.tree = tree;
        }
        this.unitsById = new Map(items.map((unit) => [unit.id, unit]));
        const { id: storedId } = readStoredOrg();
        const parentId =
          items.find((unit) => unit.unitLevel === "parent_company")?.id || null;

        const desired =
          [selectId, this.selectedId, storedId, parentId, items?.[0]?.id].find(
            (id) => id && this.unitsById.has(id),
          ) || null;

        this.selectedId = desired;
        const orgName = this.unitsById.get(desired)?.name || null;
        writeStoredOrg(desired, orgName);
        this.orgList = this.tree.flatMap((node) => [
          {
            label: node.name,
            value: node.name,
          },
          ...(node.children || []).map((child) => ({
            label: child.name,
            value: child.name,
          })),
        ]);

        kpiTargetsStore.selectedOrganization =
          this.orgList.length > 0 ? this.orgList[0].value : null;
      });
      return payload;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to load organization structure.",
      );
      runInAction(() => {
        this.error = message;
        if (groupBy === "businessType") {
          this.businessTypeTree = [];
        } else if (groupBy === "country") {
          this.countryTree = [];
        } else {
          this.tree = [];
          this.unitsById.clear();
          this.selectedId = null;
          writeStoredOrg(null, null);
        }
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  downloadImportTemplate = async ({ companyId, level } = {}) => {
    runInAction(() => {
      this.downloadingTemplate = true;
      this.error = "";
    });

    try {
      const blob = await organizationService.downloadImportTemplate({
        companyId,
        level,
      });

      downloadBlob(blob, "Organization Units Data Import.xlsx");
      return true;
    } catch (error) {
      const message = normalizeMessage(error, "Unable to download template.");
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.downloadingTemplate = false;
      });
    }
  };

  importUnitsExcel = async (importSource, options = {}) => {
    const { adminAll = false, groupBy, companyId } = options;

    if (!importSource) throw new Error("Excel file is required.");

    runInAction(() => {
      this.importing = true;
      this.error = "";
    });

    try {
      const sanitizedPayload = isSanitizedOrganizationImportPayload(
        importSource,
      )
        ? importSource
        : await sanitizeOrganizationImportFile(importSource);

      const { file: sanitizedFile, dataRows } = sanitizedPayload;

      if (!dataRows) {
        throw new Error(
          "No filled rows found in Excel. Please fill at least one row.",
        );
      }

      const form = new FormData();
      form.append("file", sanitizedFile);
      if (companyId && companyId !== "All") form.append("companyId", companyId);

      const res = await organizationService.importUnitsExcel(form);

      const result = res?.data ?? res;

      if (adminAll) await this.fetchHierarchyAdminAll({ groupBy });
      else await this.fetchHierarchy({ groupBy });

      return result;
    } catch (error) {
      const message = normalizeMessage(error, "Unable to import Excel.");
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.importing = false;
      });
    }
  };

  createUnit = async (payload, options = {}) => {
    const {
      showToast: shouldToast = true,
      toastMessages = {},
      groupBy,
      adminAll = false,
    } = options;
    runInAction(() => {
      this.saving = true;
      this.error = "";
    });
    try {
      const actionPromise = organizationService.createUnit(payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: i18n.t("Saving organization unit..."),
          success: i18n.t("Organization unit added."),
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;
      const payloadData = resolvePayload(data) || {};
      const unit = payloadData?.unit || payloadData;

      if (adminAll) {
        await this.fetchHierarchyAdminAll({ groupBy });
      } else {
        await this.fetchHierarchy({
          selectId: unit?.id ?? null,
          groupBy,
        });
      }

      return unit;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to create organization unit.",
      );
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  updateUnit = async (unitId, payload, options = {}) => {
    if (!unitId) {
      throw new Error("Unit id is required.");
    }
    const {
      showToast: shouldToast = true,
      toastMessages = {},
      groupBy,
      adminAll = false,
    } = options;
    runInAction(() => {
      this.saving = true;
      this.error = "";
    });
    try {
      const actionPromise = organizationService.updateUnit(unitId, payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: i18n.t("Saving organization unit..."),
          success: i18n.t("Organization unit updated."),
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;
      const payloadData = resolvePayload(data) || {};
      const unit = payloadData?.unit || payloadData;

      if (adminAll) {
        await this.fetchHierarchyAdminAll({ groupBy });
      } else {
        await this.fetchHierarchy({
          selectId: unit?.id ?? unitId,
          groupBy,
        });
      }
      return unit;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to update organization unit.",
      );
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  deleteUnit = async (unitId, options = {}) => {
    if (!unitId) return false;
    const {
      showToast: shouldToast = true,
      toastMessages = {},
      groupBy,
      adminAll = false,
    } = options;
    runInAction(() => {
      this.deletingId = unitId;
      this.error = "";
    });
    try {
      const actionPromise = organizationService.deleteUnit(unitId);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: i18n.t("Deleting organization unit..."),
          success: i18n.t("Organization unit deleted."),
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      await actionPromise;

      if (adminAll) {
        await this.fetchHierarchyAdminAll({ groupBy });
      } else {
        await this.fetchHierarchy({ groupBy });
      }

      return true;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to delete organization unit.",
      );
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.deletingId = null;
      });
    }
  };

  toggleStatus = async (unitId, nextStatus, options = {}) => {
    if (!unitId) return false;
    const {
      showToast: shouldToast = true,
      toastMessages = {},
      groupBy,
      adminAll = false,
    } = options;
    runInAction(() => {
      this.togglingId = unitId;
      this.error = "";
    });
    try {
      const actionPromise = organizationService.setStatus(unitId, nextStatus);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading:
            nextStatus === "active"
              ? i18n.t("Activating organization unit...")
              : i18n.t("Deactivating organization unit..."),
          success:
            nextStatus === "active"
              ? i18n.t("Organization unit activated.")
              : i18n.t("Organization unit deactivated."),
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      await actionPromise;

      if (adminAll) {
        await this.fetchHierarchyAdminAll({ groupBy });
      } else {
        await this.fetchHierarchy({ selectId: unitId, groupBy });
      }

      return true;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to update organization unit status.",
      );
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.togglingId = null;
      });
    }
  };

  get levelFilterOptions() {
    return [
      { value: "All", label: "All" },
      { value: "Level 1", label: "Level 1" },
      { value: "Level 2", label: "Level 2" },
      { value: "Level 3", label: "Level 3" },
    ];
  }

  setFilter = (key, value) => {
    const next = typeof value === "string" ? value : String(value ?? "");
    this.filters = { ...this.filters, [key]: next };
    if (key === "search") this.searchInput = next;
    this.pageIndex = 1;
  };

  _clearSearchDebounce = () => {
    if (!this._searchDebounceTimer) return;
    clearTimeout(this._searchDebounceTimer);
    this._searchDebounceTimer = null;
  };

  setSearchInput = (value) => {
    this.searchInput = value;

    this._clearSearchDebounce();
    this._searchDebounceTimer = setTimeout(() => {
      this.setFilter("search", String(value || "").trim());
      this._searchDebounceTimer = null;
    }, 350);
  };

  clearFilters = () => {
    this._clearSearchDebounce();
    this.searchInput = "";
    this.filters = { companyId: "All", level: "All", search: "" };
    this.pageIndex = 1;
  };

  get filteredItems() {
    const companyId = String(this.filters.companyId || "All");
    const level = String(this.filters.level || "All");
    const search = String(this.filters.search || "")
      .trim()
      .toLowerCase();

    const requiredUnitLevel =
      level === "All" ? null : `level_${level.split(" ")[1]}`;

    return this.items.filter((unit) => {
      if (!unit) return false;

      if (unit.unitLevel === "parent_company") return false;

      if (companyId !== "All") {
        const unitCompanyId = String(
          unit.companyId ?? unit.company?.id ?? unit.company ?? "",
        );
        if (!unitCompanyId || unitCompanyId !== companyId) return false;
      }

      if (requiredUnitLevel && unit.unitLevel !== requiredUnitLevel)
        return false;

      if (search) {
        const hay = [
          unit.code,
          unit.name,
          unit.country,
          unit.region,
          unit.unitLevel,
          unit.unitType,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!hay.includes(search)) return false;
      }

      return true;
    });
  }

  fetchHierarchyAdminAll = async (options = {}) => {
    runInAction(() => {
      this.loading = true;
      this.error = "";
    });

    try {
      const data = await organizationService.fetchHierarchyAdminAll({
        throwOnError: false,
        ...options,
      });

      const payload = resolvePayload(data) || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const tree = Array.isArray(payload.tree) ? payload.tree : [];

      runInAction(() => {
        this.tree = tree;
        this.unitsById = new Map(items.map((unit) => [unit.id, unit]));
      });

      return payload;
    } catch (error) {
      const message = normalizeMessage(error, "Unable to load organizations.");
      runInAction(() => {
        this.error = message;
        this.tree = [];
        this.unitsById.clear();
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  setPage = (v) => {
    const n = Number(v);
    this.pageIndex = Number.isFinite(n) && n >= 1 ? n : 1;
  };

  setPageSize = (v) => {
    const n = Number(v);
    this.pageSize = Number.isFinite(n) && n > 0 ? n : 10;
    this.pageIndex = 1;
  };

  get total() {
    return (this.filteredItems ?? this.items ?? []).length;
  }

  get paginatedItems() {
    const rows = this.filteredItems ?? this.items ?? [];
    const start = (this.pageIndex - 1) * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }
}

export const organizationStore = new OrganizationStore();
